import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, ShieldCheck, ArrowUpRight, Search, SlidersHorizontal, X } from 'lucide-react';
import { universities as fallbackUnis } from '../data/mock';
import { useUniversities } from '../hooks/useUniversities';
import SEO from '../components/SEO';

const BASE = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (src) => (src && src.startsWith('/') ? `${BASE}${src}` : src);

export default function UniversitiesList() {
  const list = useUniversities(fallbackUnis);
  const universities = list.length ? list : fallbackUnis;
  const [query, setQuery] = useState('');
  const [feeFilter, setFeeFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState('all');
  const [sort, setSort] = useState('rank');

  const regions = useMemo(() => {
    const set = new Set(universities.map((u) => u.region));
    return ['all', ...Array.from(set).sort()];
  }, [universities]);

  const filtered = useMemo(() => {
    let list = universities.filter((u) => {
      const q = query.toLowerCase();
      const match = !q || u.name.toLowerCase().includes(q) || u.city.toLowerCase().includes(q) || u.short.toLowerCase().includes(q);
      const feeMatch = feeFilter === 'all'
        || (feeFilter === 'low' && u.annualFeeUSD < 3800)
        || (feeFilter === 'mid' && u.annualFeeUSD >= 3800 && u.annualFeeUSD < 5000)
        || (feeFilter === 'high' && u.annualFeeUSD >= 5000);
      const regionMatch = regionFilter === 'all' || u.region === regionFilter;
      return match && feeMatch && regionMatch;
    });
    if (sort === 'fee') list.sort((a, b) => a.annualFeeUSD - b.annualFeeUSD);
    if (sort === 'name') list.sort((a, b) => a.name.localeCompare(b.name));
    if (sort === 'year') list.sort((a, b) => a.established - b.established);
    return list;
  }, [query, feeFilter, regionFilter, sort, universities]);

  const clear = () => { setQuery(''); setFeeFilter('all'); setRegionFilter('all'); setSort('rank'); };

  return (
    <div className="bg-background">
      <SEO
        title="Top 64 Medical Universities in Russia — NMC Approved | Russian Education"
        description="Explore all 64 NMC-approved government medical universities in Russia. Compare fees, ranking, city, hostel & Indian food availability. Direct MBBS admission with WHO-recognised degree."
        path="/universities"
        keywords="medical universities in Russia, NMC approved universities Russia, best MBBS university Russia, MBBS Russia fees comparison"
      />
      {/* Hero */}
      <section className="relative bg-primary text-white py-14 md:py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1547448415-e9f5b28e570d?w=1920&q=80" alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/90 to-primary/70" />
        </div>
        <div className="relative container mx-auto max-w-7xl px-4 md:px-8">
          <div className="text-xs md:text-sm uppercase tracking-[0.25em] text-brand font-bold">Premium Institutions</div>
          <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-bold mt-3 leading-[1.05]">
            Top <span className="text-brand">{universities.length}+</span> Medical Universities in Russia
          </h1>
          <p className="mt-5 text-white/75 max-w-2xl text-base md:text-lg">
            Explore our carefully curated network of WHO / NMC approved Russian government medical universities.
            Every partner is verified on quality, accommodation, cost and Indian student outcomes.
          </p>

          <div className="mt-8 max-w-xl relative">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search 64 universities by name or city (e.g. Kazan, Moscow)"
              className="w-full h-12 pl-10 pr-4 rounded-full bg-white text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand text-sm"
            />
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 border-b border-border bg-white sticky top-[80px] z-30 shadow-sm">
        <div className="container mx-auto max-w-7xl px-4 md:px-8 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-primary font-semibold text-sm">
            <SlidersHorizontal className="w-4 h-4" /> Filters:
          </div>

          <select value={feeFilter} onChange={(e) => setFeeFilter(e.target.value)}
            className="h-9 px-3 rounded-lg border border-border bg-white text-sm font-medium focus:outline-none focus:border-brand">
            <option value="all">All Fees</option>
            <option value="low">Low (&lt; $3,800/yr)</option>
            <option value="mid">Medium ($3,800–$5,000)</option>
            <option value="high">Premium (&gt; $5,000/yr)</option>
          </select>

          <select value={regionFilter} onChange={(e) => setRegionFilter(e.target.value)}
            className="h-9 px-3 rounded-lg border border-border bg-white text-sm font-medium focus:outline-none focus:border-brand max-w-[220px]">
            {regions.map((r) => (
              <option key={r} value={r}>{r === 'all' ? 'All Regions' : r}</option>
            ))}
          </select>

          <select value={sort} onChange={(e) => setSort(e.target.value)}
            className="h-9 px-3 rounded-lg border border-border bg-white text-sm font-medium focus:outline-none focus:border-brand">
            <option value="rank">Sort: Ranking</option>
            <option value="fee">Sort: Fee (Low–High)</option>
            <option value="name">Sort: Name (A–Z)</option>
            <option value="year">Sort: Oldest First</option>
          </select>

          {(query || feeFilter !== 'all' || regionFilter !== 'all' || sort !== 'rank') && (
            <button onClick={clear} className="h-9 px-3 rounded-lg border border-brand text-brand text-sm font-semibold flex items-center gap-1 hover:bg-brand hover:text-white transition-colors">
              <X className="w-3.5 h-3.5" /> Clear
            </button>
          )}

          <div className="ml-auto text-sm text-muted-foreground">
            <span className="font-bold text-primary">{filtered.length}</span> of {universities.length} universities
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="py-10 md:py-14">
        <div className="container mx-auto max-w-7xl px-4 md:px-8">
          {filtered.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground">
              <p className="text-lg">No universities match your filters.</p>
              <button onClick={clear} className="mt-3 text-brand font-bold hover:text-brand-dark">Clear filters</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {filtered.map((u) => (
                <Link to={`/universities/${u.slug}`} key={u.slug} className="group bg-white rounded-2xl overflow-hidden border border-border card-lift block">
                  <div className="relative aspect-[4/3] overflow-hidden bg-muted">
                    <img src={u.building} alt={u.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/10 to-transparent" />

                    <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur">
                      <ShieldCheck className="w-3.5 h-3.5 text-brand" strokeWidth={2.5} />
                      <span className="text-[10px] font-bold uppercase tracking-wider text-primary">WHO / NMC</span>
                    </div>

                    <div className="absolute top-3 right-3 w-11 h-11 rounded-full bg-white shadow-lg flex items-center justify-center font-serif text-sm font-bold text-brand border-2 border-brand">
                      {u.logo}
                    </div>

                    <div className="absolute bottom-3 left-3 right-3 text-white">
                      <div className="flex items-center gap-1 text-xs opacity-95">
                        <MapPin className="w-3 h-3" /> {u.city}, Russia
                      </div>
                    </div>
                  </div>

                  <div className="p-4">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-brand font-bold">
                      <span>{u.ranking}</span> · <span className="text-muted-foreground">Est. {u.established}</span>
                    </div>
                    <h3 className="font-serif text-base font-bold text-primary leading-snug mt-1.5 group-hover:text-brand transition-colors line-clamp-2">
                      {u.name}
                    </h3>
                    <div className="mt-3 pt-3 border-t border-border flex items-baseline justify-between">
                      <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Tuition / yr</span>
                      <span className="font-serif text-lg font-bold text-brand">{u.fee}</span>
                    </div>
                    <div className="mt-3 text-xs text-brand font-bold flex items-center gap-1">
                      View Details <ArrowUpRight className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
